import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Lightbulb,
  Send,
  Zap,
  ExternalLink,
  Loader2,
} from "lucide-react";
import {
  FileTextOutlined,
  PieChartOutlined,
  BarChartOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import insightsService, { Insight } from "../../services/insightsService";

interface InsightCardProps {
  insight: Insight;
}

interface InsightsLandingProps {}

const InsightCard: React.FC<InsightCardProps> = ({ insight }) => {
  const isIssue = insight.type === "issue";
  const hasChange = typeof insight.change === "number";
  const changeValue = insight.change ?? 0;
  const isPositive = changeValue > 0;

  // Format metric label for better readability
  const getMetricLabel = (metric: string) => {
    const metricMap: Record<string, string> = {
      'skew': 'Skew Value',
      'numSegments': 'Segment Count',
      'ROI': 'ROI',
      'iROAS': 'iROAS',
      'Efficiency': 'Efficiency',
      'Bid Adjustment': 'Bid Adjustment',
      'Lift Percentage': 'Lift %',
    };
    return metricMap[metric] || metric;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 transition-all hover:shadow-lg flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            {getMetricLabel(insight.metric)}
          </span>
          <span className="text-4xl font-bold text-gray-900 dark:text-white">
            {insight.value}
          </span>
        </div>
        {/* <div
          className={`flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
            isPositive
              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
          }`}
        >
          {isPositive ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          <span>{Math.abs(changeValue)}%</span>
        </div> */}
        {hasChange && (
          <div
            className={`flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
              isPositive
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            {isPositive ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            <span>{Math.abs(insight.change!)}%</span>
          </div>
        )}
      </div>

      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 leading-tight">
        {insight.title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 flex-grow">
        {insight.suggestion}
      </p>
      <div className="flex justify-end mt-auto">
        <button
          onClick={(e) => {
            e.stopPropagation();
            const actionPrompt = isIssue
              ? `Help me fix this issue: ${insight.title}. ${insight.suggestion}`
              : `Help me capitalize on this opportunity: ${insight.title}. ${insight.suggestion}`;
            const event = new CustomEvent("insightAction", {
              detail: { prompt: actionPrompt },
            });
            window.dispatchEvent(event);
          }}
          className={`rounded-lg text-sm font-medium flex items-center space-x-1 px-3 py-1.5 transition-colors ${
            isIssue
              ? "text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              : "text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
          }`}
        >
          <span>Act Now</span>
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const InsightsLanding: React.FC<InsightsLandingProps> = () => {
  const [inputValue, setInputValue] = useState<string>("");
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSend = () => {
    if (inputValue.trim()) {
      navigate(`/agent/${inputValue}`);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Fetch insights from backend
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call the new performanceAgentInsight API
        // Tenant ID is handled by backend middleware
        // Job Config ID is hardcoded in the service
        const fetchedInsights =
          await insightsService.fetchPerformanceAgentInsights();

        setInsights(fetchedInsights);
      } catch (err: any) {
        console.error("Failed to fetch Performance Agent insights:", err);
        setError(err.message);
        // Use fallback sample data on error
        setInsights(getSampleInsights());
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, []);

  React.useEffect(() => {
    const handleInsightAction = (event: any) => {
      const prompt = event.detail?.prompt;
      if (prompt) {
        navigate(`/agent/${prompt}`);
      }
    };
    window.addEventListener("insightAction", handleInsightAction);
    return () => {
      window.removeEventListener("insightAction", handleInsightAction);
    };
  }, [navigate]);
  const getSampleInsights = (): Insight[] => [
    // Issues
    {
      id: "1",
      category: "channel",
      type: "opportunity",
      title: "Meta showing strong growth",
      metric: "ROI",
      value: "1.4x",
      suggestion: "Scale up Meta budget by 20-40% [ROI is Effect/Spend]",
      priority: "high",
    },
    {
      id: "2",
      category: "segment",
      type: "opportunity",
      title: "Female millennials and zoomers segment showing growth",
      metric: "Bid Multiplier",
      value: "17%",
      suggestion:
        "Increase bid values for females aged between 25-34 in West Virginia",
      priority: "high",
    },
    {
      id: "3",
      category: "geo",
      type: "opportunity",
      title: "California showing good growth",
      metric: "Lift Percentage",
      value: "8%",
      suggestion: "Increase marketing spend in California region",
      priority: "medium",
    },

    // Issues
    {
      id: "4",
      category: "channel",
      type: "issue",
      title: "Google is underperforming",
      metric: "ROI",
      value: "0.78x",
      suggestion:
        "Reallocate 20% of Google spend to Meta [ROI is Effect/Spend]",
      priority: "high",
    },
    {
      id: "5",
      category: "segment",
      type: "issue",
      title: "Boomers segment showing decline",
      metric: "Bid Multiplier",
      value: "9%",
      suggestion: "Reduce bid values for males aged 65+ in Minnesota",
      priority: "high",
    },
    {
      id: "6",
      category: "geo",
      type: "issue",
      title: "North Carolina showing less growth",
      metric: "Lift Percentage [Expected]",
      value: "4.8%",
      suggestion: "Reduce marketing spend in North Carolina region",
      priority: "medium",
    },
  ];
  const cards = [
    {
      title: "Performance Agent",
      description: "One-click optimization.",
      icon: FileTextOutlined,
      iconBg: "bg-indigo-50",
      iconText: "text-indigo-600",
      path: "/agent/Allocate%20media%20across%20multiple%20channels?step=1",
    },
    {
      title: "MMM Agent",
      description: "Marketing Mix Modeling",
      icon: PieChartOutlined,
      iconBg: "bg-emerald-50",
      iconText: "text-emerald-600",
      path: "/planning/marketing-mix-model-landing",
    },
    {
      title: "Bid Optimization Agent",
      description: "Smart bid strategies.",
      icon: BarChartOutlined,
      iconBg: "bg-amber-50",
      iconText: "text-amber-600",
      path: "/optimization/landing",
    },
    {
      title: "Synthetic Control Agent",
      description: "Lift Studies",
      icon: LineChartOutlined,
      iconBg: "bg-sky-50",
      iconText: "text-sky-600",
      path: "/measurement/geo-lift",
    },
  ];

  const issues = insights.filter((i) => i.type === "issue");
  const opportunities = insights.filter((i) => i.type === "opportunity");

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3">
        <div className="mx-auto flex flex-col gap-3 sm:flex-row items-center sm:justify-between">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Adrsta Agent Insights
          </h1>
          <div className="w-full sm:w-[65%] bg-white dark:bg-gray-800">
            <div className="flex flex-col sm:flex-row items-start gap-3">
              <div className="relative flex-1">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your performance insights..."
                  rows={1}
                  className="w-full min-h-[36px] px-4 py-1 pr-10 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full resize-none focus:outline-none text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500"
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim()}
                  className="absolute right-0 -top-[16.9px] translate-y-1/2 w-16 h-[35px] bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 rounded-full flex items-center justify-center transition-colors disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>

              <button
                onClick={() =>
                  navigate(
                    "/agent/Allocate media across multiple channels"
                  )
                }
                className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full text-sm font-semibold transition-colors flex items-center justify-center sm:justify-start space-x-2 shadow-lg"
              >
                <Zap className="w-5 h-5" />
                <span>Run Performance Agent</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
          {cards.map((card) => (
            <div
              onClick={() => navigate(card.path)}
              key={card.title}
              className="w-full min-h-[80px] bg-white flex items-center gap-3 rounded-xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 px-4 py-1 border border-slate-100"
            >
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.iconBg}`}
              >
                <card.icon className={`text-xl ${card.iconText}`} />
              </div>

              <div className="space-y-1">
                <h3 className="text-md font-semibold text-slate-800">
                  {card.title}
                </h3>
                <p className="text-sm text-slate-500 leading-snug">
                  {card.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className=" mx-auto space-y-8">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Loading insights...
              </span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Unable to load live insights. Showing sample data. Error:{" "}
                {error}
              </p>
            </div>
          )}

          {/* Opportunities Section */}
          {!loading && opportunities.length > 0 && (
            <section>
              <div className="flex items-center space-x-2 mb-4">
                <Lightbulb className="w-6 h-6 text-green-600 dark:text-green-400" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Opportunities
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {opportunities.map((insight) => (
                  <InsightCard key={insight.id} insight={insight} />
                ))}
              </div>
            </section>
          )}

          {/* Issues Section */}
          {!loading && issues.length > 0 && (
            <section>
              <div className="flex items-center space-x-2 mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Issues
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {issues.map((insight) => (
                  <InsightCard key={insight.id} insight={insight} />
                ))}
              </div>
            </section>
          )}

          {/* No Insights State */}
          {!loading && insights.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Lightbulb className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-lg text-gray-600 dark:text-gray-400">
                No insights available yet
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                Run your first MMM analysis to see insights
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsightsLanding;
